import { useMemo, useState } from "react";
import { Cell, Pie, PieChart, Tooltip, ResponsiveContainer } from "recharts";
import InvModal from "components/modals/InvModal";
import { Badge, Btn, Card, SumCard, useIsMobile } from "components/ui/index";
import { T } from "theme/index";
import { fmtIDR, fmtDec, fmtShort } from "utils/format";
import { TYPE_META } from "constants/index";
import { getInvested, getCurrent } from "engine/investments";
import { getQty, getActiveInvestments, getTotalRealizedGain } from "engine/investmentSales";
import { fetchPrice } from "utils/calc";

const GROUP_COLORS = ["#E8A21C","#22C55E","#3B82F6","#EF4444","#8B5CF6","#F97316","#06B6D4","#EC4899","#10B981","#6B7280"];

export default function Investasi({ investments, investmentSales = [], onAdd, onEdit, onDelete, onBulkEdit, onSell }) {
  const isMobile = useIsMobile();
  const [modal,        setModal]        = useState(null);
  const [fetching,     setFetching]     = useState(false);
  const [lastSync,     setLastSync]     = useState(null);
  const [collapsed,    setCollapsed]    = useState(() => {
    // Default semua tertutup
    const init = {};
    return init; // akan diisi saat byGroup pertama kali render
  });

  // Inisialisasi semua group sebagai collapsed saat pertama render
  const getCollapsed = (name) => collapsed[name] !== false; // default true kecuali explicitly dibuka
  const [editingGroup, setEditingGroup] = useState(null); // { oldName, newName }

  const activeInvestments = useMemo(() => getActiveInvestments(investments), [investments]);
  const totalRealizedGain = useMemo(() => getTotalRealizedGain(investmentSales), [investmentSales]);

  const totalInv = activeInvestments.reduce((a,i) => a + getInvested(i), 0);
  const totalCur = activeInvestments.reduce((a,i) => a + getCurrent(i),  0);
  const totalGL  = totalCur - totalInv;
  const pct      = totalInv > 0 ? (totalGL / totalInv) * 100 : 0;
  const pctColor = totalGL >= 0 ? T.green : T.red;

  const existingGroups = useMemo(() =>
    [...new Set(investments.map(i => i.group).filter(Boolean))].sort(),
  [investments]); // eslint-disable-line react-hooks/exhaustive-deps

  const byGroup = useMemo(() => {
    const map = {};
    activeInvestments.forEach(i => {
      const g = i.group || "Lainnya";
      if (!map[g]) map[g] = { items:[] };
      map[g].items.push(i);
    });
    return Object.entries(map).map(([name, d]) => ({
      name, items: d.items,
      invested: d.items.reduce((a,i) => a + getInvested(i), 0),
      current:  d.items.reduce((a,i) => a + getCurrent(i),  0),
    }));
  }, [activeInvestments]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedSales = useMemo(() =>
    [...investmentSales].sort((a,b) => new Date(b.date) - new Date(a.date)),
  [investmentSales]);

  // Sale terakhir (urutan tersimpan) per investmentId — dipakai buat badge "posisi habis"
  const lastSaleIdByInv = useMemo(() => {
    const map = {};
    investmentSales.forEach(s => { map[s.investmentId] = s.id; });
    return map;
  }, [investmentSales]);
  const invById = useMemo(() => {
    const map = {};
    investments.forEach(i => { map[i.id] = i; });
    return map;
  }, [investments]);
  const isSoldOutBadge = (sale) => {
    const inv = invById[sale.investmentId];
    return lastSaleIdByInv[sale.investmentId] === sale.id && inv && getQty(inv) <= 0;
  };

  // Pie chart data
  const pieData = byGroup.map((g, gi) => ({
    name: g.name,
    value: g.current,
    color: GROUP_COLORS[gi % GROUP_COLORS.length],
    pct: totalCur > 0 ? (g.current / totalCur) * 100 : 0,
  })).filter(d => d.value > 0).sort((a,b) => b.value - a.value);

  const syncAll = async () => {
    setFetching(true);
    const updated = [...investments];
    for (let i = 0; i < updated.length; i++) {
      const inv = updated[i];
      const canFetch = ((inv.type==="saham_idx"||inv.type==="saham_us"||inv.type==="forex") && inv.ticker)
        || (inv.type==="crypto" && inv.coinId) || inv.type==="emas";
      if (!canFetch) continue;
      const price = await fetchPrice(inv);
      if (price != null) updated[i] = { ...inv, currentPrice: price };
    }
    onBulkEdit(updated);
    setLastSync(new Date());
    setFetching(false);
  };

  const toggleCollapse = (name) => setCollapsed(p => ({ ...p, [name]: p[name] === false })); // false=open → true=closed; undefined/true=closed → false=open

  const startEditGroup = (name, e) => {
    e.stopPropagation();
    setEditingGroup({ oldName: name, newName: name });
  };

  const saveEditGroup = () => {
    if (!editingGroup) return;
    const { oldName, newName } = editingGroup;
    if (newName.trim() && newName.trim() !== oldName) {
      onBulkEdit(investments.map(i => i.group === oldName ? { ...i, group: newName.trim() } : i));
    }
    setEditingGroup(null);
  };

  return (
    <div>
      {modal && (
        <InvModal
          inv={modal.mode==="add" ? null : modal.inv}
          mode={modal.mode==="add" ? "new" : modal.mode}
          existingGroups={existingGroups}
          onSave={modal.mode==="add" ? onAdd : onEdit}
          onSell={(sale, updatedInv) => { onSell(sale, updatedInv); }}
          onClose={() => setModal(null)}
        />
      )}

      {/* Summary cards */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(3,1fr)", gap:10, marginBottom:16 }}>
        <SumCard label="Total Modal" value={totalInv} color={T.blue} />
        <SumCard label="Nilai Pasar" value={totalCur} color={T.gold} />
        <div style={{ gridColumn:isMobile?"1/-1":"auto" }}>
          <SumCard label={`G/L ${pct>=0?"+":""}${pct.toFixed(1)}%`} value={totalGL} color={pctColor} prefix={totalGL>=0?"+":"-"} />
        </div>
      </div>

      {/* Pie chart alokasi per kelompok */}
      {investments.length > 0 && pieData.length > 1 && (
        <Card style={{ padding:16, marginBottom:14 }}>
          <div style={{ color:T.text, fontWeight:700, fontSize:13, marginBottom:12 }}>🥧 Alokasi per Kelompok</div>
          <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
            <ResponsiveContainer width={isMobile?"100%":180} height={isMobile?180:180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                  dataKey="value" paddingAngle={2}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtIDR(v)}
                  contentStyle={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, fontSize:11 }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex:1, minWidth:140 }}>
              {pieData.map(d => (
                <div key={d.name} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <div style={{ width:10, height:10, borderRadius:"50%", background:d.color, flexShrink:0 }} />
                  <div style={{ flex:1, fontSize:12, color:T.text }}>{d.name}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.pct.toFixed(1)}%</div>
                  <div style={{ fontSize:11, color:T.muted, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{isMobile ? fmtShort(d.value) : fmtIDR(d.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Action bar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, gap:8, flexWrap:"wrap" }}>
        <div>
          <div style={{ color:T.text, fontWeight:700, fontSize:14, textWrap:'balance' }}>Portofolio</div>
          {lastSync && <div style={{ color:T.muted, fontSize:10, marginTop:2 }}>Sync: {lastSync.toLocaleTimeString("id-ID")}</div>}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => { if(window.confirm("Hapus semua instrumen?")) onBulkEdit([]); }}
            style={{ background:T.red+"15", border:`1px solid ${T.red}28`, borderRadius:8, color:T.red, cursor:"pointer", padding:"9px 12px", fontSize:13, fontWeight:700, WebkitTapHighlightColor:"transparent" }}>
            🗑 Hapus Semua
          </button>
          <button onClick={syncAll} disabled={fetching || investments.length===0}
            style={{ background:T.blue+"18", border:`1px solid ${T.blue}33`, borderRadius:8, color:T.blue, cursor:"pointer", padding:"9px 12px", fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:5, opacity:(fetching||investments.length===0)?0.4:1, WebkitTapHighlightColor:"transparent" }}>
            {fetching ? "⏳ Syncing..." : "🔄 Sync Harga"}
          </button>
          <Btn onClick={() => setModal({ mode:"add" })} style={{ fontSize:13, padding:"9px 12px" }}>＋ Tambah</Btn>
        </div>
      </div>

      {investments.length === 0 && (
        <Card style={{ padding:52, textAlign:"center" }}>
          <div style={{ fontSize:44, marginBottom:12 }}>📈</div>
          <div style={{ color:T.text, fontWeight:700, fontSize:15, marginBottom:6 }}>Tambah instrumen investasimu</div>
          <div style={{ color:T.muted, fontSize:12, marginBottom:20, lineHeight:1.6 }}>Saham IDX/US, Crypto, Reksa Dana, Emas,<br/>atau aset lainnya — semua bisa kamu kelola di sini.</div>
          <Btn onClick={() => setModal({ mode:"add" })} style={{ margin:"0 auto" }}>＋ Tambah Instrumen</Btn>
        </Card>
      )}

      {byGroup.map((group, gi) => {
        const groupGL  = group.current - group.invested;
        const groupPct = group.invested > 0 ? (groupGL / group.invested) * 100 : 0;
        const isCollapsed = getCollapsed(group.name);
        const isEditing   = editingGroup?.oldName === group.name;

        return (
          <Card key={group.name} style={{ marginBottom:14 }}>
            {/* Group header — clickable to collapse */}
            <div onClick={() => !isEditing && toggleCollapse(group.name)}
              style={{ padding:"12px 14px", borderBottom: isCollapsed ? "none" : `1px solid ${T.border}`, cursor:"pointer", userSelect:"none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:6 }}>
                {/* Left: editable group name */}
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:isCollapsed?T.muted:T.gold, fontSize:12, transition:"transform 0.2s" }}>
                    {isCollapsed ? "▶" : "▼"}
                  </span>
                  {isEditing ? (
                    <input autoFocus value={editingGroup.newName}
                      onChange={e => setEditingGroup(p => ({ ...p, newName: e.target.value }))}
                      onBlur={saveEditGroup}
                      onKeyDown={e => { if(e.key==="Enter") saveEditGroup(); if(e.key==="Escape") setEditingGroup(null); }}
                      onClick={e => e.stopPropagation()}
                      style={{ background:T.surface, border:`1px solid ${T.gold}`, borderRadius:6, color:T.text, fontSize:13, fontWeight:700, padding:"2px 8px", outline:"none", width:140 }}
                    />
                  ) : (
                    <span style={{ color:T.gold, fontWeight:700, fontSize:13 }}>{group.name}</span>
                  )}
                  <button onClick={(e) => startEditGroup(group.name, e)}
                    title="Edit nama kelompok" aria-label={`Edit nama kelompok ${group.name}`}
                    style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:11, padding:"6px 8px", WebkitTapHighlightColor:"transparent", lineHeight:1, minWidth:44, minHeight:44 }}>
                    ✏️
                  </button>
                </div>
                {/* Right: stats */}
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                  <div style={{ display:"flex", gap:10, fontSize:11 }}>
                    <span style={{ color:T.muted }}>Modal <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtIDR(group.invested)}</span></span>
                    <span style={{ color:T.muted }}>Nilai <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtIDR(group.current)}</span></span>
                  </div>
                  <span style={{ fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:11, color:groupGL>=0?T.green:T.red }}>
                    {groupGL>=0?"+":"-"}{fmtIDR(Math.abs(groupGL))} ({groupPct>=0?"+":""}{groupPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Instrument list — collapsed or expanded */}
            {!isCollapsed && group.items.map(inv => {
              const invested = getInvested(inv);
              const current  = getCurrent(inv);
              const gl       = current - invested;
              const glPct    = invested > 0 ? (gl / invested) * 100 : 0;
              const canFetch = ((inv.type==="saham_idx"||inv.type==="saham_us"||inv.type==="forex") && inv.ticker)
                || (inv.type==="crypto" && inv.coinId) || inv.type==="emas";
              const qtyLabel = inv.type==="emas" ? "gram" : inv.type==="crypto" ? "koin" : "unit";
              const qty      = inv.type==="saham_idx" ? `${fmtDec(inv.lot||0)} lot` : `${fmtDec(inv.unit||0)} ${qtyLabel}`;
              const meta     = TYPE_META[inv.type] || TYPE_META.lainnya;

              return (
                <div key={inv.id} style={{ padding:"14px 14px 10px", borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                    {/* Left: icon + name + badge */}
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10, flex:1, minWidth:0 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:meta.color+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{meta.icon}</div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ color:T.text, fontWeight:600, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{inv.name}</div>
                        <div style={{ display:"flex", gap:5, alignItems:"center", marginTop:3, flexWrap:"wrap" }}>
                          <Badge label={meta.label} color={meta.color} />
                          {inv.ticker && <span style={{ color:T.muted, fontSize:10, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{inv.ticker}</span>}
                        </div>
                        <div style={{ color:T.muted, fontSize:11, marginTop:3 }}>
                          Qty: <span style={{ color:T.text }}>{qty}</span>
                          {" · "}Avg: <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtDec(inv.avgPrice)}</span>
                          {" · "}Now: <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtDec(inv.currentPrice)}</span>
                        </div>
                        {inv.notes && <div style={{ color:T.muted, fontSize:10, marginTop:2 }}>{inv.notes}</div>}
                      </div>
                    </div>
                    {/* Right: value + G/L */}
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:13 }}>{fmtIDR(current)}</div>
                      <div style={{ color:gl>=0?T.green:T.red, fontSize:10 }}>
                        {gl>=0?"▲":"▼"} {Math.abs(glPct).toFixed(1)}%
                      </div>
                      <div style={{ color:gl>=0?T.green:T.red, fontSize:10, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>
                        {gl>=0?"+":"-"}{fmtIDR(Math.abs(gl))}
                      </div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div style={{ display:"flex", gap:6, marginTop:10, justifyContent:"flex-end" }}>
                    {canFetch && (
                      <button onClick={async () => {
                        const price = await fetchPrice(inv);
                        if (price != null) onEdit({ ...inv, currentPrice: price });
                      }} style={{ background:T.blue+"15", border:`1px solid ${T.blue}33`, borderRadius:6, color:T.blue, cursor:"pointer", padding:"9px 12px", fontSize:12, fontWeight:700, minHeight:44, WebkitTapHighlightColor:"transparent" }}>
                        🔄 Fetch
                      </button>
                    )}
                    <button onClick={() => setModal({ mode:"buy", inv })}
                      style={{ background:T.green+"15", border:`1px solid ${T.green}33`, borderRadius:6, color:T.green, cursor:"pointer", padding:"9px 12px", fontSize:12, fontWeight:700, minHeight:44, WebkitTapHighlightColor:"transparent" }}>
                      ＋ Beli Lagi
                    </button>
                    <button onClick={() => setModal({ mode:"sell", inv })}
                      style={{ background:T.orange+"15", border:`1px solid ${T.orange}33`, borderRadius:6, color:T.orange, cursor:"pointer", padding:"9px 12px", fontSize:12, fontWeight:700, minHeight:44, WebkitTapHighlightColor:"transparent" }}>
                      💰 Jual
                    </button>
                    <button onClick={() => setModal({ mode:"edit", inv })}
                      style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, color:T.muted, cursor:"pointer", padding:"9px 12px", fontSize:12, minHeight:44, WebkitTapHighlightColor:"transparent" }}>
                      ✏️ Edit
                    </button>
                    <button onClick={() => { if(window.confirm(`Hapus ${inv.name}?`)) onDelete(inv.id); }}
                      style={{ background:T.red+"15", border:`1px solid ${T.red}28`, borderRadius:6, color:T.red, cursor:"pointer", padding:"9px 12px", fontSize:12, minHeight:44, WebkitTapHighlightColor:"transparent" }}>
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </Card>
        );
      })}

      {/* Riwayat Terjual — per transaksi jual, bukan per instrumen */}
      {sortedSales.length > 0 && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ padding:"12px 14px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6 }}>
            <span style={{ color:T.text, fontWeight:700, fontSize:13 }}>📜 Riwayat Terjual</span>
            <span style={{ fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:12, color:totalRealizedGain>=0?T.green:T.red }}>
              Total Realized: {totalRealizedGain>=0?"+":"-"}{fmtIDR(Math.abs(totalRealizedGain))}
            </span>
          </div>
          {sortedSales.map(sale => {
            const meta = TYPE_META[sale.type] || TYPE_META.lainnya;
            const qtyLabel = sale.type==="saham_idx" ? "lot" : sale.type==="emas" ? "gram" : sale.type==="crypto" ? "koin" : "unit";
            return (
              <div key={sale.id} style={{ padding:"12px 14px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                    <span style={{ color:T.text, fontWeight:600, fontSize:13 }}>{meta.icon} {sale.name}</span>
                    {isSoldOutBadge(sale) && <Badge label="posisi habis" color={T.muted} />}
                  </div>
                  <div style={{ color:T.muted, fontSize:11, marginTop:3 }}>
                    {fmtDec(sale.qty)} {qtyLabel} @ {fmtDec(sale.sellPrice)} · {new Date(sale.date).toLocaleDateString("id-ID")}
                  </div>
                  {sale.notes && <div style={{ color:T.muted, fontSize:10, marginTop:2 }}>{sale.notes}</div>}
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ color:sale.realizedGain>=0?T.green:T.red, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:13 }}>
                    {sale.realizedGain>=0?"+":"-"}{fmtIDR(Math.abs(sale.realizedGain))}
                  </div>
                  <div style={{ color:T.muted, fontSize:10 }}>Proceeds: {fmtShort(sale.proceeds)}</div>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
